/**
 * Leaf module — Supabase Realtime channel health registry.
 * Drives adaptive polling when WebSocket subscriptions fail silently.
 */

import { isRealtimeSuppressed } from '../network/connectivityPause';

const HEALTHY = 'healthy';
const UNHEALTHY = 'unhealthy';
const UNKNOWN = 'unknown';

/** Ignore CLOSED briefly after our own teardown (removeChannel / unmount). */
const TEARDOWN_GRACE_MS = 750;
/** Require consecutive failure signals before marking unhealthy. */
const CONSECUTIVE_FAILURES_FOR_UNHEALTHY = 2;
/** Reset failure streak after quiet period. */
const FAILURE_STREAK_RESET_MS = 45_000;
/** Cap failure streak tracking — avoids escalating xN logs and retry pressure. */
const MAX_CHANNEL_FAILURE_STREAK = 8;

/** @type {Map<string, 'healthy' | 'unhealthy' | 'unknown'>} */
const channelStatus = new Map();
/** @type {Map<string, number>} channelName -> ignore CLOSED until timestamp */
const intentionalTeardownUntil = new Map();
/** @type {Map<string, { count: number, firstAt: number }>} */
const channelFailureStreak = new Map();
const listeners = new Set();

function emitChange() {
  listeners.forEach((listener) => listener());
}

function setChannelStatus(channelName, status) {
  const prev = channelStatus.get(channelName);
  if (prev === status) return;
  channelStatus.set(channelName, status);
  emitChange();
}

function isIntentionalTeardown(channelName) {
  const until = intentionalTeardownUntil.get(channelName);
  if (!until) return false;
  if (Date.now() > until) {
    intentionalTeardownUntil.delete(channelName);
    return false;
  }
  return true;
}

function resetChannelFailures(channelName) {
  channelFailureStreak.delete(channelName);
}

function recordChannelFailure(channelName, status, err) {
  if (!channelName || isIntentionalTeardown(channelName)) return false;

  if (isRealtimeSuppressed()) {
    return false;
  }

  const now = Date.now();
  let streak = channelFailureStreak.get(channelName);
  if (!streak || now - streak.firstAt > FAILURE_STREAK_RESET_MS) {
    streak = { count: 0, firstAt: now, loggedUnhealthy: false };
  }

  if (streak.count >= MAX_CHANNEL_FAILURE_STREAK) {
    channelFailureStreak.set(channelName, streak);
    return false;
  }

  streak.count += 1;
  channelFailureStreak.set(channelName, streak);

  if (streak.count < CONSECUTIVE_FAILURES_FOR_UNHEALTHY) {
    return false;
  }

  setChannelStatus(channelName, UNHEALTHY);

  if (!streak.loggedUnhealthy) {
    streak.loggedUnhealthy = true;
    channelFailureStreak.set(channelName, streak);
    if (err) {
      console.warn(`[realtime] ${channelName} ${status}:`, err);
    } else {
      console.warn(
        `[realtime] ${channelName} ${status} (failures capped at ${MAX_CHANNEL_FAILURE_STREAK})`,
      );
    }
  }

  return true;
}

/** Call before removeChannel so the resulting CLOSED is not treated as a fault. */
export function markChannelTeardown(channelName) {
  if (!channelName) return;
  intentionalTeardownUntil.set(channelName, Date.now() + TEARDOWN_GRACE_MS);
  resetChannelFailures(channelName);
  if (channelStatus.delete(channelName)) {
    emitChange();
  }
}

/** @deprecated Prefer markChannelTeardown + removeTrackedChannel */
export function clearRealtimeChannelStatus(channelName) {
  markChannelTeardown(channelName);
}

export function removeTrackedChannel(supabase, channel, channelName) {
  markChannelTeardown(channelName);
  if (channel && supabase) {
    supabase.removeChannel(channel);
  }
}

export function reportRealtimeChannelStatus(channelName, status) {
  if (!channelName) return;
  const normalized =
    status === HEALTHY || status === UNHEALTHY || status === UNKNOWN ? status : UNKNOWN;
  setChannelStatus(channelName, normalized);
}

export function getRealtimeHealth() {
  if (channelStatus.size === 0) return UNKNOWN;
  const statuses = [...channelStatus.values()];
  if (statuses.some((s) => s === UNHEALTHY)) return UNHEALTHY;
  if (statuses.every((s) => s === HEALTHY)) return HEALTHY;
  return UNKNOWN;
}

export function isRealtimeUnhealthy() {
  return getRealtimeHealth() === UNHEALTHY;
}

export function subscribeRealtimeHealth(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const FAILURE_STATUSES = new Set(['CHANNEL_ERROR', 'TIMED_OUT', 'CLOSED']);

/**
 * Subscribe to a Supabase channel and report health from status callbacks.
 * @returns {import('@supabase/supabase-js').RealtimeChannel}
 */
export function subscribeWithHealth(channel, channelName) {
  whenRealtimeAuthed(channel.socket, () => {
    // Removed while the token was on its way: subscribing now would bring it back.
    if (channel.socket.getChannels().includes(channel)) channel.subscribe(onStatus);
  });
  return channel;

  function onStatus(status, err) {
    if (status === 'SUBSCRIBED') {
      resetChannelFailures(channelName);
      intentionalTeardownUntil.delete(channelName);
      setChannelStatus(channelName, HEALTHY);
      return;
    }

    if (!FAILURE_STATUSES.has(status)) return;

    if (status === 'CLOSED' && isIntentionalTeardown(channelName)) {
      return;
    }

    recordChannelFailure(channelName, status, err);
  }
}

/**
 * Run `subscribe` once the realtime socket holds the session's token (2026-10-31, sim phones). realtime-js 2.95 reads
 * the token into a channel's join the moment subscribe() is called, while the socket's first connect fetches it
 * asynchronously; the queued join keeps the empty payload and nothing re-sends the token until it changes (a refresh,
 * up to an hour). So the first channel of a cold start joined as anon, and RLS dropped every row for you without an
 * error: the Header's notifications channel never heard "join_approved", and a just-approved joiner's Find stayed solo.
 *
 * Asked every time, not only when the socket has no token: on a resume the socket still holds the token from before
 * the pocket, already expired. A channel re-subscribed then joined on it, the wake's refresh landed 0.2 s later, and
 * realtime-js only re-sends a new token to channels that have finished joining - so it stayed on the dead one (sim
 * phones, token-expiry). setAuth() asks auth-js for the session (getSession refreshes one about to run out) first.
 * No socket (test doubles): subscribe now.
 */
export function whenRealtimeAuthed(socket, subscribe) {
  if (!socket?.setAuth) {
    subscribe();
    return;
  }
  socket
    .setAuth()
    .catch(() => {})
    .then(subscribe);
}

/**
 * A topic of its own for each subscribe (2026-10-27 latency audit). Re-subscribing under the SAME name right after
 * removeChannel got the old channel back: removal waits for the server's leave reply, realtime-js's channel() returns any
 * channel with that topic, and subscribe() is a no-op unless it is closed - so on every other resume the notifications
 * channel joined nothing and no notification reached the phone until the next resume. Health is still keyed on the
 * stable name; only the wire topic is fresh.
 */
let topicSeq = 0;
export function freshTopic(channelName) {
  topicSeq += 1;
  return `${channelName}#${topicSeq}`;
}

/** The last reported state of one channel: 'healthy', 'unhealthy', or 'unknown' (never subscribed, or torn down). */
export function getChannelStatus(channelName) {
  return channelStatus.get(channelName) ?? UNKNOWN;
}

/** Stable channel name helper for group message subscriptions. */
export function groupMessagesChannelName(groupId) {
  return `group:${groupId}:messages`;
}
